import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  alpha,
  Box,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import LogoDialog from "../../../../components/Administration/Organization/OrganizationDetails/LogoDialog";
import LogoImage from "../../../../components/Administration/Organization/OrganizationDetails/LogoImage";
import getCompanyLogoUrl from "../../../../helpers/getCompanyLogoUrl";
import getCompanyMailLogoUrl from "../../../../helpers/getCompanyMailLogoUrl";

const OrganizationBranding = ({
  companyId,
  disabled,
  logoRefreshKey,
}) => {
  const intl = useIntl();

  const [companyLogoTime, setCompanyLogoTime] = useState(Date.now());
  const [companyMailLogoTime, setCompanyMailLogoTime] = useState(Date.now());
  const [logoDialogOpen, setLogoDialogOpen] = useState(false);

  useEffect(() => {
    setCompanyLogoTime(Date.now());
    setCompanyMailLogoTime(Date.now());
  }, [companyId, logoRefreshKey]);

  const platformLogoPreview = companyId
    ? getCompanyLogoUrl(companyId, companyLogoTime)
    : "";

  const emailLogoPreview = companyId
    ? getCompanyMailLogoUrl(companyId, companyMailLogoTime)
    : "";

  const handlePlatformLogoUploaded = () => {
    setCompanyLogoTime(Date.now());
  };

  const handleEmailLogoUploaded = () => {
    setCompanyMailLogoTime(Date.now());
  };

  return (
    <>
      <Box
        sx={{
          px: { xs: 1.35, sm: 1.6 },
          pt: { xs: 1.35, sm: 1.6 },
          pb: { xs: 1.2, sm: 1.45 },
          bgcolor: theme => alpha(theme.palette.secondary.main, 0.9),
          transition: "background-color 180ms ease",
          "@media (hover: hover)": {
            "&:hover": { bgcolor: "secondary.main" },
          },
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Box sx={{ minWidth: 0, minHeight: 32, display: "flex", alignItems: "center" }}>
            <LogoImage
              src={platformLogoPreview}
              alt={intl.formatMessage({
                id: "admin.companySettings.companyLogoPlatform.Title",
              })}
              maxWidth={160}
              maxHeight={30}
              placeholderSize={16}
            />
          </Box>

          <Tooltip title={<FormattedMessage id="admin.organizationDetails.change" />}>
            <span>
              <IconButton
                disabled={disabled}
                size="small"
                onClick={() => setLogoDialogOpen(true)}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 0.7,
                  color: "secondary.contrastText",
                  bgcolor: "rgba(255, 255, 255, 0.16)",
                  "&:hover": { bgcolor: "rgba(255, 255, 255, 0.40)" },
                  transition: "background-color 180ms ease",
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <LogoDialog
        open={logoDialogOpen}
        onClose={() => setLogoDialogOpen(false)}
        disabled={disabled}
        companyId={companyId}
        platformLogoPreview={platformLogoPreview}
        emailLogoPreview={emailLogoPreview}
        onPlatformUploaded={handlePlatformLogoUploaded}
        onEmailUploaded={handleEmailLogoUploaded}
      />
    </>
  );
};

OrganizationBranding.propTypes = {
  companyId: PropTypes.number,
  disabled: PropTypes.bool,
  logoRefreshKey: PropTypes.number.isRequired,
};

OrganizationBranding.defaultProps = {
  companyId: null,
  disabled: false,
};

OrganizationBranding.displayName = "OrganizationBranding";

export default OrganizationBranding;
